import { Item } from '@sitecore-content-sdk/nextjs';
import { BaseContentResponse } from 'lib/services/models/content-response';
import { SearchResponse } from 'lib/services/models/search-response';
import { SearchFacetGroup } from 'lib/services/content-service';
import { parseContentListItem } from 'lib/utils/components/content-list-data';
import { useContentItem } from 'lib/hooks/useContentItem';
import { normalizeGuid } from 'lib/utils/string-utils';
import { throttle } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SearchRequest } from 'src/pages/api/search/content-list';
import { useFavoritesApiIds } from './useFavoritesApiIds';
import { useFacetUrlSync } from 'lib/hooks/search/useFacetUrlSync';
import { resolveContentPageSize } from '../CardListingShared';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';

export interface ContentQueryContextProps {
  query?: SearchRequest | null;
  setQuery?: (query: SearchRequest) => void;
  searchResponse: SearchResponse | null;
  isLoading: boolean;
}

const EMPTY_SEARCH_RESPONSE: SearchResponse = {
  Results: [],
  Count: 0,
  PageIndex: 1,
  PageSize: 0,
  SearchFacets: null,
};

const buildContentListSearchUrl = (searchQuery: SearchRequest) => {
  const searchParams = new URLSearchParams();

  Object.entries(searchQuery).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'string') {
      searchParams.set(key, value);
      return;
    }

    searchParams.set(key, JSON.stringify(value));
  });

  const queryString = searchParams.toString();

  return queryString ? `/api/search/content-list?${queryString}` : '/api/search/content-list';
};

const ContentQueryContext = createContext<ContentQueryContextProps>({
  searchResponse: null,
  isLoading: false,
});

export const useContentQuery = () => {
  return useContext(ContentQueryContext);
};

const normalizeFacetGroups = (facetGroups?: SearchFacetGroup[] | null) => {
  return (facetGroups ?? [])
    .map((group) => ({
      name: group.name,
      facets: [...(group.facets ?? [])]
        .map((facet) => ({
          name: facet.name,
          facetId: facet.facetId,
        }))
        .sort((left, right) => left.facetId.localeCompare(right.facetId)),
    }))
    .filter((group) => group.facets.length > 0)
    .sort((left, right) => left.name.localeCompare(right.name));
};

const normalizeFacetId = (facetId: string) => normalizeGuid(facetId) ?? facetId.toLowerCase();

const getFacetIds = (facetGroups?: SearchFacetGroup[] | null) => {
  return normalizeFacetGroups(facetGroups)
    .flatMap((group) => group.facets)
    .map((facet) => normalizeFacetId(facet.facetId));
};

const findFacetGroupName = (
  facetGroups: SearchFacetGroup[] | null | undefined,
  facetId: string
) => {
  const normalizedFacetId = normalizeFacetId(facetId);

  return normalizeFacetGroups(facetGroups).find((group) =>
    group.facets.some((facet) => normalizeFacetId(facet.facetId) === normalizedFacetId)
  )?.name;
};

export const shouldResetPageIndexForFacetChange = (
  previousFacetGroups?: SearchFacetGroup[] | null,
  nextFacetGroups?: SearchFacetGroup[] | null
) => {
  return (
    JSON.stringify(normalizeFacetGroups(previousFacetGroups)) !==
    JSON.stringify(normalizeFacetGroups(nextFacetGroups))
  );
};

export const normalizeQueryPageIndex = (
  previousQuery: SearchRequest | null,
  nextQuery: SearchRequest
) => {
  if (shouldResetPageIndexForFacetChange(previousQuery?.facetGroups, nextQuery.facetGroups)) {
    return {
      ...nextQuery,
      pageIndex: 1,
    };
  }

  return nextQuery;
};

export const getOptimisticFacetResultCount = ({
  previousQuery,
  nextQuery,
  searchResponse,
}: {
  previousQuery: SearchRequest | null;
  nextQuery: SearchRequest;
  searchResponse: SearchResponse | null;
}) => {
  const previousFacetIds = getFacetIds(previousQuery?.facetGroups);
  const nextFacetIds = getFacetIds(nextQuery.facetGroups);
  const previousFacetIdSet = new Set(previousFacetIds);
  const nextFacetIdSet = new Set(nextFacetIds);

  const addedFacetIds = nextFacetIds.filter((facetId) => !previousFacetIdSet.has(facetId));
  const removedFacetIds = previousFacetIds.filter((facetId) => !nextFacetIdSet.has(facetId));

  if (addedFacetIds.length !== 1 || removedFacetIds.length !== 0) {
    return undefined;
  }

  const addedFacetCount = searchResponse?.SearchFacets?.find(
    (facet) => normalizeFacetId(facet.Id) === addedFacetIds[0]
  )?.RecordCount;

  if (addedFacetCount === undefined) {
    return undefined;
  }

  if (previousFacetIds.length === 0) {
    return addedFacetCount;
  }

  const addedFacetGroupName = findFacetGroupName(nextQuery.facetGroups, addedFacetIds[0]);

  if (!addedFacetGroupName) {
    return addedFacetCount;
  }

  const hasExistingFacetInSameGroup = normalizeFacetGroups(previousQuery?.facetGroups).some(
    (group) => group.name === addedFacetGroupName && group.facets.length > 0
  );

  if (!hasExistingFacetInSameGroup) {
    return addedFacetCount;
  }

  return (searchResponse?.Count ?? 0) + addedFacetCount;
};

// ---------------------------------------------------------------------------
// ContentQueryProvider — manages query state and ties API data to search.
// ---------------------------------------------------------------------------
export const ContentQueryProvider = ({
  children,
  contentListItem,
  initialSearchResponse,
  withFacets = false,
  searchConfigurationId,
  runtimeSearchTerm,
  enableFavoritesAPI,
  pageSize: pageSizeProp,
}: {
  children: React.ReactNode;
  contentListItem?: Item;
  initialSearchResponse: SearchResponse | null;
  /**
   * When true, populate the grid from the user's saved favorites via /api/favorites.
   * When false (default), use the normal content-list datasource flow.
   */
  withFacets?: boolean;
  searchConfigurationId?: string;
  runtimeSearchTerm?: string;
  enableFavoritesAPI?: boolean;
  /**
   * Explicit page size for favorites mode. When provided it is used as the
   * initial page size so the API returns one page of results rather than all
   * saved items at once. Typically sourced from staticProps.result.PageSize.
   */
  pageSize?: number;
}) => {
  const [searchResponse, setSearchResponse] = useState<SearchResponse | null>(
    initialSearchResponse ?? null
  );
  const [isLoading, setIsLoading] = useState(false);
  const latestSearchIdRef = useRef(0);
  const searchCacheRef = useRef(new Map<string, SearchResponse>());
  const contentItemId = (useContentItem() as BaseContentResponse | undefined)?.ContentId;
  const { locale } = useGlobalData();

  const contentResult = useMemo(() => {
    return parseContentListItem(contentListItem);
  }, [contentListItem]);

  const baseQuery = useMemo((): SearchRequest | null => {
    // When a runtime search term is provided (e.g. from SearchResults reading
    // the URL), we can build a valid query even without a content-list
    // datasource. This ensures the grid fires a search request.
    if (!contentResult && !runtimeSearchTerm) {
      return null;
    }

    const initialPageSize = resolveContentPageSize(pageSizeProp, initialSearchResponse?.PageSize);
    return {
      contentIds: contentResult?.contentIds ?? [],
      metadataIds: contentResult?.metadataIds ?? [],
      contentTypes: contentResult ? [contentResult.contentType] : ['All'],
      locale,
      relatedContentType: contentResult?.relatedContentType,
      contentItemId: contentItemId,
      searchTerm: runtimeSearchTerm ?? contentResult?.searchTerm ?? '',
      searchConfigurationId,
      sortBy: contentResult?.sortBy,
      sortDirection: contentResult?.sortDirection,
      getFacets: withFacets,
      pageSize: initialPageSize,
      taggedSearchMatchAll: contentResult?.matchAllTags,
    };
  }, [
    contentItemId,
    contentResult,
    initialSearchResponse,
    locale,
    runtimeSearchTerm,
    searchConfigurationId,
    withFacets,
    pageSizeProp,
  ]);

  const initialQuery = useMemo(() => {
    return baseQuery;
  }, [baseQuery]);

  const [query, setQueryState] = useState<SearchRequest | null>(initialQuery ?? null);
  const selectedFacetsFromUrl = useFacetUrlSync(query);

  useEffect(() => {
    if (initialQuery !== null && query === null) {
      setQueryState(initialQuery);
    }
    // Only re-run when initialQuery reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  // When the runtime search term changes (e.g. user navigates to a new
  // search URL), reset the query to page 1 with the updated term.
  useEffect(() => {
    if (!baseQuery || runtimeSearchTerm === undefined) return;

    setQueryState((currentQuery) => {
      if (currentQuery?.searchTerm === baseQuery.searchTerm) return currentQuery;
      return { ...baseQuery, pageIndex: 1 };
    });
  }, [baseQuery, runtimeSearchTerm]);

  const favoritesApiEnabled = !!enableFavoritesAPI;

  const favoriteContentIds = useFavoritesApiIds({
    enabled: favoritesApiEnabled,
    // In favorites mode show ALL content types — the data-source's contentType filter
    // must not restrict which saved items are returned (articles, recipes, etc.).
    contentType: favoritesApiEnabled ? undefined : initialQuery?.contentTypes?.[0],
  });

  const mergeFavoritesQuery = useCallback(
    (nextQuery: SearchRequest | null): SearchRequest | null => {
      if (!favoritesApiEnabled || favoriteContentIds === null) {
        return nextQuery;
      }

      if (favoriteContentIds.length === 0) {
        return {
          ...(nextQuery ?? {}),
          contentIds: [],
          pageSize: 0,
        };
      }

      // Slice the full favorites list to only the IDs needed for the current page.
      // This keeps the request small regardless of how many total favorites exist.
      const effectivePageSize = resolveContentPageSize(nextQuery?.pageSize, pageSizeProp);
      const pageIndex = nextQuery?.pageIndex ?? 1;
      const start = (pageIndex - 1) * effectivePageSize;
      const pageIds = favoriteContentIds.slice(start, start + effectivePageSize);

      return {
        ...(nextQuery ?? {}),
        // Use 'All' content type so recipes, articles, and products are all
        // returned — the contentIds list is the only meaningful filter needed.
        contentTypes: ['All' as import('lib/services/models/types').ContentType],
        // Only the IDs for this page — avoids URL/request-body length limits.
        contentIds: pageIds,
        pageSize: effectivePageSize,
        // Always request page 1 from the API since we pre-sliced to the right IDs.
        pageIndex: 1,
      };
    },
    [favoriteContentIds, favoritesApiEnabled, pageSizeProp]
  );

  useEffect(() => {
    if (selectedFacetsFromUrl === null) {
      return;
    }

    setQueryState((currentQuery) => {
      const base = currentQuery ?? initialQuery;
      if (!base) {
        return currentQuery;
      }

      const nextFacetGroups = selectedFacetsFromUrl;
      if (!shouldResetPageIndexForFacetChange(base.facetGroups, nextFacetGroups)) {
        return currentQuery;
      }

      // Store raw query — effectiveQuery handles the favorites merge.
      return {
        ...base,
        facetGroups: nextFacetGroups,
        locale: base.locale ?? locale,
        pageIndex: 1,
      };
    });
  }, [initialQuery, locale, mergeFavoritesQuery, selectedFacetsFromUrl]);

  const effectiveQuery = useMemo(() => {
    return mergeFavoritesQuery(query);
  }, [mergeFavoritesQuery, query]);

  const setQuery = useCallback(
    (nextQuery: SearchRequest) => {
      const normalizedQuery = normalizeQueryPageIndex(query, {
        ...nextQuery,
        locale: nextQuery.locale ?? query?.locale ?? locale,
      });
      const optimisticFacetResultCount = getOptimisticFacetResultCount({
        previousQuery: query,
        nextQuery: normalizedQuery,
        searchResponse,
      });

      if (optimisticFacetResultCount !== undefined) {
        setSearchResponse((currentResponse) => {
          if (!currentResponse) {
            return currentResponse;
          }

          return {
            ...currentResponse,
            Count: optimisticFacetResultCount,
            PageIndex: normalizedQuery.pageIndex ?? 1,
          };
        });
      }

      // Store the raw query so the real pageIndex is preserved in state.
      // effectiveQuery will apply mergeFavoritesQuery (which slices contentIds
      // to the correct page) — doing it here too would overwrite pageIndex to 1.
      setQueryState(normalizedQuery);
    },
    [locale, query, searchResponse]
  );

  // Stable throttled search function — recreating throttle inside effects resets the
  // timer every render, preventing the fetch from ever firing reliably.
  const doSearchRef = useRef(
    throttle(
      async (
        searchQuery: SearchRequest,
        searchId: number,
        onSuccess: (data: SearchResponse, completedSearchId: number) => void,
        onComplete: (completedSearchId: number) => void
      ) => {
        try {
          const url = buildContentListSearchUrl(searchQuery);

          // SSRF guard: ensure the constructed URL resolves to the expected internal
          // API path only. new URL() normalises path-traversal sequences before comparison.
          const parsed = new URL(url, 'http://localhost');
          if (parsed.pathname !== '/api/search/content-list') {
            console.error(
              '[ContentQueryProvider] Blocked unexpected URL pathname:',
              parsed.pathname
            );
            onComplete(searchId);
            return;
          }

          const result = await fetch(url, { method: 'GET' });
          const data = (await result.json()) as SearchResponse;

          searchCacheRef.current.set(url, data);
          onSuccess(data, searchId);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[ContentQueryProvider] Failed to fetch content list:', error);
        } finally {
          onComplete(searchId);
        }
      },
      500
    )
  );

  const doSearch = useCallback(
    (searchQuery: SearchRequest, transform?: (r: SearchResponse) => SearchResponse) => {
      // Return instantly from cache — no network request, no loading flicker.
      const url = buildContentListSearchUrl(searchQuery);
      const cached = searchCacheRef.current.get(url);
      if (cached) {
        setSearchResponse(transform ? transform(cached) : cached);
        return;
      }

      const searchId = latestSearchIdRef.current + 1;
      latestSearchIdRef.current = searchId;
      setIsLoading(true);

      doSearchRef.current(
        searchQuery,
        searchId,
        (data, completedSearchId) => {
          if (completedSearchId === latestSearchIdRef.current) {
            setSearchResponse(transform ? transform(data) : data);
          }
        },
        (completedSearchId) => {
          if (completedSearchId === latestSearchIdRef.current) {
            setIsLoading(false);
          }
        }
      );
    },
    []
  );

  // Favorites API mode — runs only when enableFavoritesAPI=true.
  useEffect(() => {
    if (!favoritesApiEnabled) return;

    if (favoriteContentIds === null) {
      // Still loading from /api/favorites — wait.
      return;
    }

    if (favoriteContentIds.length === 0) {
      setSearchResponse(EMPTY_SEARCH_RESPONSE);
      setIsLoading(false);
      return;
    }

    if (!effectiveQuery) return;

    // The query only contains IDs for the current page (pre-sliced in mergeFavoritesQuery).
    // Patch the response so pagination UI sees the correct total count and page index.
    const requestedPageIndex = query?.pageIndex ?? 1;
    const effectivePageSize = resolveContentPageSize(effectiveQuery.pageSize, pageSizeProp);
    const totalCount = favoriteContentIds.length;

    doSearch(effectiveQuery, (raw) => ({
      ...raw,
      Count: totalCount,
      PageIndex: requestedPageIndex,
      PageSize: effectivePageSize,
    }));
  }, [
    doSearch,
    effectiveQuery,
    favoriteContentIds,
    favoritesApiEnabled,
    pageSizeProp,
    query?.pageIndex,
  ]);

  // Normal content-list flow — runs only when enableFavoritesAPI=false.
  useEffect(() => {
    if (favoritesApiEnabled) return;
    if (!query) return;

    // Re-fetch when the user changes the query (sorting/pagination/filtering).
    // Also fetch on first load if there is no pre-fetched static data.
    if (query !== initialQuery || !initialSearchResponse) {
      doSearch(query);
    } else {
      // Seed cache with the server-rendered response so that returning to the
      // base query (e.g. after deselecting all facets) is instant.
      searchCacheRef.current.set(buildContentListSearchUrl(query), initialSearchResponse);
    }
  }, [doSearch, favoritesApiEnabled, initialQuery, initialSearchResponse, query]);

  return (
    <ContentQueryContext.Provider value={{ query, setQuery, searchResponse, isLoading }}>
      {children}
    </ContentQueryContext.Provider>
  );
};
